import { css } from 'styled-components'

/**
 * Shared rendering rules for react-markdown (+ remark-gfm) output inside a
 * chat Bubble. Chat bubbles set `white-space: pre-wrap` so plain-text (human)
 * messages keep their line breaks — but that also makes the literal "\n" text
 * nodes react-markdown leaves between block elements render as blank lines
 * inside lists, and collapses GFM tables to unstyled inline rows. Keep the
 * counter-rules in one place so every AI chat panel renders markdown
 * identically.
 *
 * Borders/tints use currentColor so the same rules work on both the
 * dark-on-light assistant bubble and the white-on-blue human bubble. Apply
 * AFTER the bubble's base `max-width`/`white-space` so the overrides win.
 */
export const markdownBubbleContent = css`
  /* Tables almost always need more horizontal room than narrative text:
     widen the bubble and allow horizontal scroll when 95% still isn't enough. */
  &:has(table) {
    max-width: 95%;
    overflow-x: auto;
  }

  & table {
    border-collapse: collapse;
    width: 100%;
    margin: 6px 0;
    font-size: 12px;
    line-height: 1.4;
    white-space: normal;
  }
  & th,
  & td {
    border: 1px solid currentColor;
    border-color: color-mix(in srgb, currentColor 25%, transparent);
    padding: 4px 8px;
    text-align: left;
    vertical-align: top;
  }
  & th {
    font-weight: 600;
    background: color-mix(in srgb, currentColor 6%, transparent);
  }

  /* react-markdown keeps literal "\n" text nodes between block elements; the
     bubble's white-space: pre-wrap would otherwise render them as blank lines
     inside lists. Reset to normal on markdown blocks. Human plain-text
     messages keep pre-wrap via the bubble itself. */
  & ul,
  & ol,
  & li,
  & p {
    white-space: normal;
  }

  /* Loose GFM lists wrap each item's text in <p>; drop that <p>'s trailing
     margin so items don't gain an extra half-line gap. */
  & li > p {
    margin: 0;
  }
`
